"use client";

import LockIcon from "@/app/components/icons/lockIcon";
import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";

/**
 * 投资组合升级提示组件 - 用于非年度会员
 */
export default function PortfolioUpgrade() {
  const theme = useTheme();
  const t = useTranslations("PortfolioUpgrade");
  const router = useRouter();

  const handleUpgrade = () => {
    router.push("/membership");
  };

  return (
    <Box
      sx={{
        maxWidth: { md: "48rem" },
        mx: "auto",
        pt: 11,
        pb: 20,
      }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
        }}>
        {/* 介绍 */}
        <Typography
          sx={{
            fontSize: "var(--font-size-l)",
            fontWeight: 600,
            mb: 1.5,
          }}>
          {t("title")}
        </Typography>
        <Typography
          sx={{
            fontSize: "var(--font-size-s)",
            mb: 6,
            lineHeight: 1.5,
          }}>
          {t.rich("description", { br: () => <br /> })}
        </Typography>

        {/* 如何跟随 */}
        <Typography
          sx={{
            fontSize: "var(--font-size-s)",
            fontWeight: 600,
            lineHeight: 1.6,
            mb: 1,
            color: "primary.main",
          }}>
          {t("howToFollow.title")}
        </Typography>

        <Box sx={{ mb: 5 }}>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("howToFollow.copyMoves.title")}
            </Typography>
            <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("howToFollow.copyMoves.description")}
            </Typography>
          </Box>
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("howToFollow.followSignals.title")}
            </Typography>
            <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6, mb: 1 }}>
              {t.rich("howToFollow.followSignals.description", { br: () => <br /> })}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "var(--font-size-s)", mb: 4 }}>
            {t("howToFollow.summary")}
          </Typography>
        </Box>

        {/* 为什么有效 */}
        <Typography
          sx={{
            fontSize: "var(--font-size-s)",
            fontWeight: 600,
            lineHeight: 1.6,
            mb: 1,
            color: "primary.main",
          }}>
          {t("whyItWorks.title")}
        </Typography>

        <Box sx={{ mb: 5 }}>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("whyItWorks.trendShifts.title")}
            </Typography>
            <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("whyItWorks.trendShifts.description")}
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("whyItWorks.protectCapital.title")}
            </Typography>
            <Typography sx={{ fontSize: "var(--font-size-s)" }}>
              {t("whyItWorks.protectCapital.description")}
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("whyItWorks.strongTrends.title")}
            </Typography>
            <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("whyItWorks.strongTrends.description")}
            </Typography>
          </Box>
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("whyItWorks.evolveMarket.title")}
            </Typography>
            <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              {t("whyItWorks.evolveMarket.description")}
            </Typography>
          </Box>
        </Box>

        {/* 透明度 */}
        <Typography
          sx={{
            fontSize: "var(--font-size-m)",
            fontWeight: 600,
            lineHeight: 1.6,
            mb: 1,
            color: "primary.main",
          }}>
          {t("transparency.title")}
        </Typography>

        <Box sx={{ mb: 5 }}>
          <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>{t("transparency.youllSee")}</Typography>
          <Box sx={{ ml: 1 }}>
            <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
              <span style={{marginRight: "0.5rem"}}>•</span> {t("transparency.features.liveWeights")} <br></br>
              <span style={{marginRight: "0.5rem"}}>•</span> {t("transparency.features.performance")} <br></br>
              <span style={{marginRight: "0.5rem"}}>•</span> {t("transparency.features.rebalance")}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>{t("transparency.summary")}</Typography>
        </Box>

        {/* 重要说明 */}
        <Typography
          sx={{
            fontSize: "var(--font-size-m)",
            fontWeight: 600,
            lineHeight: 1.6,
            mb: 1,
            color: "primary.main",
          }}>
          {t("importantNote.title")}
        </Typography>

        <Box sx={{ mb: 6 }}>
          <Typography sx={{ fontSize: "var(--font-size-s)", lineHeight: 1.6 }}>
            {t.rich("importantNote.content", { br: () => <br /> })}
          </Typography>
        </Box>

        {/* 解锁按钮 */}
        <Box sx={{ textAlign: "center", mt: 10, mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleUpgrade}
            sx={{
              px: 4.5,
              py: 1.5,
              fontWeight: 600,
            }}>
            <LockIcon width="1.5rem" height="1.5rem" color="#F2F2F2" sx={{ mr: 1 }} />
            {t("unlockBtn")}
          </Button>
        </Box>

        {/* 底部说明 */}
        <Typography
          sx={{
            fontSize: "var(--font-size-xs)",
            lineHeight: 1.6,
          }}>
          {t.rich("footer", { strong: (chunks) => <strong>{chunks}</strong> })}
        </Typography>
      </Box>
    </Box>
  );
}
